import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SessionData } from "@/modules/eventRegistration/features/EventDirectory/api/fetchEventDetails/types";
import {
  formatDateRangeInTimeZone,
  formatTimeRangeInTimeZone,
} from "@/modules/eventRegistration/features/EventDirectory/utils/dateUtils";

function buildMapsUrl(
  address?: string | null,
  city?: string | null,
  state?: string | null,
  postalCode?: string | null,
): string {
  const fullAddress = [address, city, state, postalCode]
    .filter(Boolean)
    .join(", ");
  return `https://maps.google.com/?q=${encodeURIComponent(fullAddress)}`;
}

interface EventDetailsSessionListProps {
  sessions: SessionData[] | null | undefined;
}

const EventDetailsSessionList: React.FC<EventDetailsSessionListProps> = ({
  sessions,
}) => {
  const navigate = useNavigate();

  if (!sessions || sessions.length === 0) {
    return null;
  }

  return (
    <div>
      <h2 className="text-base font-normal text-gray-900 dark:text-white mb-3">
        Sessions
      </h2>
      <div className="space-y-3">
        {sessions.map((session) => {
          const dateRange = formatDateRangeInTimeZone(
            session.startDate || null,
            session.endDate || null,
            session.timezoneIdentifier || null,
          );
          const timeRange = formatTimeRangeInTimeZone(
            session.startDate || null,
            session.endDate || null,
            session.timezoneIdentifier || null,
          );
          const mainTimeDisplay = session.timezoneShortName
            ? `${timeRange} (${session.timezoneShortName})`
            : timeRange;

          const seats = session.availableSeats;
          let availabilityText = "";
          let availabilityClass = "text-xs text-gray-400 dark:text-gray-500";
          if (!session.isFinalized && !session.isCancelled && typeof seats === "number") {
            if (seats > 0) {
              availabilityText = `${seats} seats left`;
            } else {
              availabilityText = "Waitlist only";
              availabilityClass = "text-xs text-amber-600 dark:text-amber-400";
            }
          }

          let venueText = "";
          let venueLink = "";
          if (session.venue) {
            const parts = [session.venue.name];
            if (session.venue.city) {
              parts.push(
                `${session.venue.city}, ${session.venue.state || ""}`.trim(),
              );
            }
            venueText = parts.filter(Boolean).join(" · ");
            venueLink = buildMapsUrl(
              session.venue.address,
              session.venue.city,
              session.venue.state,
              session.venue.postalCode,
            );
          } else if (session.isVirtualOnly) {
            venueText = "Virtual only";
          }

          const isClickable = !session.isFinalized && !session.isCancelled;

          return (
            <div
              className={`group bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-4 ${
                isClickable
                  ? "cursor-pointer hover:border-gray-300 dark:hover:border-slate-600 transition-colors"
                  : "opacity-60"
              }`}
              key={session.id}
              onClick={
                isClickable
                  ? () =>
                      navigate(
                        `/event-registration/events/${session.uuid}/register`,
                      )
                  : undefined
              }
            >
              {/* Session name */}
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {session.name || "Session"}
              </div>

              {/* Date, time, timezone */}
              <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {dateRange} · {mainTimeDisplay}
              </div>

              {/* Venue */}
              {venueText && (
                <div className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {venueLink ? (
                    <a
                      className="hover:text-gray-900 dark:hover:text-white transition-colors"
                      href={venueLink}
                      onClick={(e) => e.stopPropagation()}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {venueText}
                    </a>
                  ) : (
                    venueText
                  )}
                </div>
              )}

              {/* Action row */}
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 dark:border-slate-700/50">
                {isClickable ? (
                  <>
                    <span className={availabilityClass}>
                      {availabilityText}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-sm text-gray-400 dark:text-gray-500 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                      Register
                      <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </>
                ) : (
                  <>
                    <span />
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      {session.isCancelled ? "Cancelled" : "Completed"}
                    </span>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EventDetailsSessionList;
